import {
  applyCallDefaults,
  generateMultiTonePalette,
  seedLookVariant,
  typographyForVibe,
  type BuildType,
  type EngagementType,
  type LeadRecord,
} from '@flashpoint/site-dna';
import type { User } from 'firebase/auth';

export interface NewLeadInput {
  id: string;
  ownerUid: string;
  ownerEmail?: string;
  businessName: string;
  industry?: string;
  phone?: string;
  email?: string;
  logicBrooksLeadId?: string;
  buildType?: BuildType;
  engagementType?: EngagementType;
}

/**
 * Build a fully-valid LeadRecord. New previews default to Demo / Spec Build
 * and apply look/sections without writing placeholder copy.
 */
export function newLeadDefaults(input: NewLeadInput): LeadRecord {
  const buildType = input.buildType ?? 'Demo / Spec Build';
  const vibe = 'The Modern';
  const now = new Date().toISOString();
  const industry = input.industry?.trim() ?? '';
  const phone = input.phone?.trim() ?? '';
  const email = input.email?.trim() ?? '';

  const base: LeadRecord = {
    id: input.id,
    ownerUid: input.ownerUid,
    leadStatus: 'Discovery',
    engagement: {
      buildType,
      engagementType: input.engagementType ?? 'Demo',
      urgency: { tier: 'Standard' },
    },
    context: {
      industryVertical: industry,
      integrations: [],
      compliance: [],
      localization: 'English Only',
    },
    domain: {
      status: 'Undecided',
    },
    content: {
      copy: 'Outline Only',
      photos: 'Stock Acceptable',
      logo: 'Have It',
    },
    siteDna: {
      businessInfo: {
        name: input.businessName,
        industryVertical: industry,
        ...(phone ? { phone } : {}),
        ...(email ? { email } : {}),
      },
      designTokens: {
        vibe,
        signature: 'nuzum-method',
        personality: [],
        // Seeded from the business name so neighboring referrals in the same
        // industry do not open on identical looks.
        lookVariant: seedLookVariant(input.businessName, industry),
        animations: { enableGradients: true, reducedMotionFallback: true },
        multiTonePalette: generateMultiTonePalette(vibe),
        typography: typographyForVibe(vibe),
      },
      assets: {},
      layout: {
        pageCount: 3,
        sectionOrder: [],
        pages: [],
      },
    },
    provisioning: {
      status: 'idle',
      mode: 'shared',
      steps: [],
    },
    createdAt: now,
    updatedAt: now,
    ...(input.logicBrooksLeadId ? { logicBrooksLeadId: input.logicBrooksLeadId } : {}),
  };

  return applyCallDefaults(base, { forceSections: true, copy: 'none' });
}

export function actorFromUser(user: User | null) {
  return {
    actorUid: user?.uid,
    actorEmail: user?.email ?? undefined,
  };
}
